import { DialogTitle } from "@headlessui/react";

import { clsx } from "clsx";

import { useId, useRef, useState, type ReactNode } from "react";
import { useOptionalParticipationFlow } from "~/components/participation/ParticipationFlow";
import type { ParticipationOperation } from "~/components/participation/state";
import { PrimaryButton } from "~/components/Button";
import { Text } from "~/components/Text";
import { CheckoutFooter } from "./CheckoutFooter";

type CheckoutQuestionStepProps = {
  title: string;
  description?: string;
  compact?: boolean;
  children: ReactNode;
  onSubmit: () => unknown | Promise<unknown>;
  operation?: ParticipationOperation;
  onBack?: () => void;
  backDisabled?: boolean;
  loading?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;


  status?: ReactNode;
  error?: string | null;
};

export function CheckoutQuestionStep({
  title,
  description,
  compact = false,
  children,
  onSubmit,
  operation,
  onBack,
  backDisabled = false,
  loading = false,
  submitDisabled = false,
  submitLabel = "Next",


  status,
  error,
}: CheckoutQuestionStepProps) {
  const flow = useOptionalParticipationFlow();
  const submission = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const busy = loading || submitting || !!flow?.pending;
  return (
    <div className={clsx("flex min-h-0 flex-1 flex-col", compact ? "mt-0" : "mt-[10px]")}>
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (submission.current || busy || submitDisabled) return;
          submission.current = true;
          setSubmitting(true);
          setSubmissionError(null);
          try {
            if (flow && operation) await flow.run(operation, async () => onSubmit());
            else await onSubmit();
          } catch {
            setSubmissionError("We couldn’t save your details. Check your connection and try again.");
          } finally {
            submission.current = false;
            setSubmitting(false);
          }
        }}
        aria-busy={busy}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className={clsx("-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-4 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0", compact ? "pt-0" : "pt-[30px]")}>
          {status && <div className="mb-4">{status}</div>}
          <Text as={DialogTitle} variant="primary">
            {title}
          </Text>
          {description && <Text
            as="p"
            variant="secondary"
            size="sm"
            className="mt-2 max-w-md leading-relaxed"
          >
            {description}
          </Text>}
          <div className="mt-5">{children}</div>
          {(error || submissionError) && (
            <Text role="alert" size="sm" color="pink" className="mt-4">
              {error || submissionError}
            </Text>
          )}
        </div>

        <CheckoutFooter onBack={onBack} backDisabled={busy || backDisabled} showTerms={false}>
          <PrimaryButton
            type="submit"
            loading={busy}
            disabled={busy || submitDisabled}
            className="text-base!"
          >
            {submitLabel}
          </PrimaryButton>
        </CheckoutFooter>
      </form>
    </div>
  );
}

type CheckoutTextFieldProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "className" | "id"
> & {
  label: string;
  error?: string;
  compact?: boolean;
};

export function CheckoutTextField({
  label,
  error,
  compact = false,
  type = "text",
  ...props
}: CheckoutTextFieldProps) {
  const id = useId();
  const messageId = useId();

  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type={type}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? messageId : undefined}
        className={clsx(
          "block w-full rounded-[9px] border bg-white px-3 font-sans text-base text-butter-green outline-none transition-colors",
          compact ? "h-11" : "h-14",
          "placeholder:font-normal placeholder:text-placeholder-text",
          error
            ? "border-butter-pink focus:border-butter-pink"
            : "border-butter-cream focus:border-butter-green/60",
        )}
        {...props}
      />
      {error && (
        <Text id={messageId} role="alert" size="sm" color="pink" className="mt-2">
          {error}
        </Text>
      )}
    </div>
  );
}
